<template>
  <v-card class="px-3">
    <v-card-text>
      <v-row>
        <p class="subheading mb-0 ml-1">
          {{ accountStatus.allowsHelpdesk ? 
            `${$t('Support access is allowed')}, ${$t(videoAllowanceInfo)}${$t(expirationDateInfo)}`
            : $t('Support access is disabled') 
          }}
        </p>
      </v-row>
      <v-divider class="my-6"></v-divider>
      <v-row>
        <div class="ml-auto">
        <v-btn v-if="accountStatus.allowsHelpdesk" @click="updateAccess(false)" color="primary">
          {{$t('Disable support access')}}
        </v-btn>
        <v-btn v-else-if="!editSupportAccess" @click="editSupportAccess = true" color="primary">
          {{ $t('Enable support access') }}
        </v-btn>
        </div>
      </v-row>
      <div class="ma-2" v-if="editSupportAccess">
        {{ $t("For how long is the support team allowed to access your account?") }}
      </div>
    </v-card-text>
    <v-row v-if="editSupportAccess">
      <v-col lg="6" md="10" sm="10">
        <v-select
          class="mx-6 pt-0 mt-0 pb-6"
          hide-details
          v-model="helpdeskTime"
          :items="helpdeskTimes"
          dense
          bottom
          :menu-props="{ top: true }"
        >
        <template v-slot:item="{ item }">
          {{ $t(item) }}
        </template>
        <template v-slot:selection="{ }">
          {{ $t(helpdeskTime) }}
        </template>
        </v-select>
      </v-col>
      <v-col v-if="!dealerDashboard" lg="6" md="12" sm="12" class="d-flex d-inline-block">
        <p class="subheading pt-1 ml-6">
            {{ $t("Show video") }}
        </p>
        <v-menu max-width="500" nudge-bottom="30" open-on-hover>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon>
              <v-icon size="20" color="primary">fas fa-info-circle</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item>
              <div>
                {{ $t("When \"Show video\" is disabled the support team can access your account, but without any video. Only blurred camera snapshots will be available to verify if the camera is working.") }}
              </div>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-switch
          hide-details
          class="mt-0 ml-4"
          v-model="showVideo"
          dense
        ></v-switch>
      </v-col>
    </v-row>
    <v-row v-if="editSupportAccess">
      <div class="ml-auto">
        <v-btn
          depressed
          color="primary"
          class="mx-4 mb-4"
          @click="updateAccess(true)"
        >
          {{ $t("Allow Access") }}
        </v-btn>
        <v-btn
          depressed
          class="mx-4 mb-4"
          @click="editSupportAccess = false"
        >
          {{ $t("Cancel") }}
        </v-btn>
      </div>
    </v-row>
  </v-card>
</template>

<script>
import { reactive, computed, toRefs } from "../plugins/compositionApi";
import { store } from "@/store";
import { t } from "../service/locale";
import { findTimeZone, getUTCOffset } from 'timezone-support'
import { getTimeStamp } from '@eencloud/core-components/src/service/time'

export default {
  name: "Helpdesk",
  props: {
    dealerDashboard: { // the DD API doesn't support getting the expirationData and setting/getting allowVideo
      type: Boolean,
      default: false
    },
    accountStatus: {
      type: Object
    }
  },
  setup(props) {
    const state = useComponentState(props);
    const { updateAccess } = useSupportAccess(state);

    return {
      ...toRefs(state),
      updateAccess
    };
  }
};

function useComponentState(props) {
  const state = reactive({
    hour: 3600000,
    day: 86400000,
    editSupportAccess: false,
    helpdeskTime: "Always",
    helpdeskTimes: computed(() => ["04 "+t("hours"), "12 "+t("hours"), "24 "+t("hours"), "48 "+t("hours"), "Always"]),
    showVideo: true,
    expirationDateInfo: computed(() => {
      if (props.dealerDashboard || props.accountStatus.allowsHelpdeskExpirationDate === "9999-12-31T23:59:59.000+0000") return ""
      return `, ${t('until')} ` + getTimeStamp(useTimezone().getOffset(store.getters.timeZone ?? "UTC"), new Date(props.accountStatus.allowsHelpdeskExpirationDate))
    }),
    videoAllowanceInfo: computed(() =>  (!props.dealerDashboard && props.accountStatus.allowsHelpdeskShowVideo) ? "including video access" : "without video access")
  });

  return state;
}

function useSupportAccess(state) {

  async function updateAccess(newValue) {
    let expirationDate = '1000-01-01T00:00:00.000+0000'

    if (newValue) {
      state.editSupportAccess = false

      switch (state.helpdeskTime.slice(0,2)) {
        case 'Al':
            expirationDate = '9999-12-31T23:59:59.000+0000'
            break
        case '04':
            expirationDate = new Date(new Date().getTime() + (4 * state.hour)).toISOString().replace('Z', '+0000')
            break
        case '12':
            expirationDate = new Date(new Date().getTime() + (12 * state.hour)).toISOString().replace('Z', '+0000')
            break
        case '24':
            expirationDate = new Date(new Date().getTime() + state.day).toISOString().replace('Z', '+0000')
            break
        case '48':
            expirationDate = new Date(new Date().getTime() + (2 * state.day)).toISOString().replace('Z', '+0000')
            break
        }
    }

    let newValues = {
      allowsHelpdesk: newValue,
      allowsHelpdeskExpirationDate: expirationDate,
      allowsHelpdeskShowVideo: state.showVideo,
    }
    
    await store.dispatch("setHelpdesk", newValues);

    await store.dispatch("toastMessage", {
      showing: true,
      text: newValue ?
        `${t("Support access has been enabled")}, ${t(state.videoAllowanceInfo)}${state.helpdeskTime !== "Always" ? t(state.expirationDateInfo) : ''}` :
        t("Support access has been disabled"),
      timeout: 5000,
      color: "primary"
    });
  }

  return { updateAccess };
}

function useTimezone() {

  function getOffset(timeZone) {
    const foundTimeZone = findTimeZone(timeZone)
    const date = new Date()
    const timeZoneOffset = getUTCOffset(date, foundTimeZone)
    return timeZoneOffset.offset
  }

  return { getOffset }
}

</script>

<style lang="scss" scoped></style>
